import FlUtil from 'flUtils';

class Subject<T> {
    private currentValue: T = null;
    private readonly subscribers: ((val: T) => void)[] = [];

    public constructor(private readonly isBehaviorSubject: boolean = false) {}

    public subscribe(callback: (val: T) => void): void {
        this.subscribers.push(callback);

        if (this.isBehaviorSubject && this.currentValue) {
            callback(this.currentValue);
        }
    }

    public next(value: T): void {
        this.currentValue = value;
        this.subscribers.forEach((subscriber: (val: T) => void) => {
            subscriber(value);
        });
    }
}

export default class HandleShopInteractionService {
    private readonly inputTriggered$: Subject<HTMLInputElement> = new Subject<HTMLInputElement>(true);
    private readonly inputChanged$: Subject<string> = new Subject<string>();
    private readonly inputAutocompleted$: Subject<HTMLInputElement> = new Subject<HTMLInputElement>();
    private readonly inputSubmitted$: Subject<Event> = new Subject<Event>();
    private readonly scroll$: Subject<void> = new Subject<void>();
    private readonly resize$: Subject<void> = new Subject<void>();

    private activeSearchInput: HTMLInputElement;
    private activeSearchInputIsInFixedContext: boolean;
    private readonly searchInputs: HTMLInputElement[];

    public constructor() {
        this.searchInputs = FlUtil?.getSearchFieldElements() ? Array.from(FlUtil.getSearchFieldElements()) : [];
        this.registerEvents();
        this.checkPossibleActiveInputOnInit();
    }

    private debounce<T>(callback: (arg: T) => void, immediate: boolean = false, wait: number = 100): (arg: T) => void {
        let timeout;

        return (arg: T) => {
            const callNow = immediate && !timeout;
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                timeout = null;
                if (!immediate) {
                    callback.call(this, arg);
                }
            }, wait);
            if (callNow) {
                callback.call(this, arg);
            }
        };
    }

    private checkPossibleActiveInputOnInit(): void {
        const activeElement = document.activeElement;
        const activeSearchElement = this.searchInputs.find((searchElement: HTMLInputElement) => searchElement === activeElement);

        if (activeSearchElement) {
            this.inputTriggered(activeSearchElement);
        }
    }

    private inputTriggered(clickedSearchField: HTMLInputElement): void {
        this.setActiveSearchInput(clickedSearchField);

        // Disable browser completion on the search field to avoid interference.
        if (!('autocomplete' in clickedSearchField.attributes)) {
            clickedSearchField.setAttribute('autocomplete', 'off');
        }

        this.activeSearchInputIsInFixedContext = this.isElementInFixedContext(clickedSearchField);
        this.inputTriggered$.next(clickedSearchField);
    }

    private registerEvents(): void {
        this.addGlobalEvents();
        this.searchInputs.forEach((searchField: HTMLInputElement) => {
            this.addSearchInteractionEvent(searchField);
            this.addSearchValueChangedEvent(searchField);
        });
    }

    private addSearchInteractionEvent(searchField: HTMLInputElement): void {
        const debouncedInputTrigger = this.debounce<EventTarget>(this.inputTriggered, true);
        searchField.addEventListener('focus', (e: FocusEvent) => debouncedInputTrigger(e.currentTarget));
        searchField.addEventListener('click', (e: MouseEvent) => debouncedInputTrigger(e.currentTarget));
        searchField.form.addEventListener('submit', (event: Event) => this.inputSubmitted$.next(event));
    }

    private addSearchValueChangedEvent(searchField: HTMLInputElement): void {
        searchField.addEventListener('keyup', (event: KeyboardEvent) => {
            // Ignore enter presses to prevent search form submissions from triggering suggestions.
            if (event.key === 'Enter') {
                return;
            }

            const triggeredSearchField = event.currentTarget as HTMLInputElement;
            this.inputChanged$.next(triggeredSearchField.value);
        });

        searchField.addEventListener('keydown', (event: KeyboardEvent) => {
            const triggeredSearchField = event.currentTarget as HTMLInputElement;

            switch (event.key) {
                case 'ArrowRight':
                    this.inputAutocompleted$.next(triggeredSearchField);
                    break;
                case 'Tab':
                    event.preventDefault();
                    this.inputAutocompleted$.next(triggeredSearchField);
                    break;
            }
        });

        searchField.addEventListener('paste', (event: ClipboardEvent) => {
            const paste = (event.clipboardData || (window as any).clipboardData).getData('text');
            this.inputChanged$.next(paste);
        });
    }

    private addGlobalEvents(): void {
        document.addEventListener('scroll', () => this.scroll$.next());
        window.addEventListener('resize', () => this.resize$.next());
    }

    private isElementInFixedContext(element: HTMLElement): boolean {
        return window.getComputedStyle(element.parentElement).position === 'fixed'
            ? true
            : element.parentElement.nodeName === 'BODY'
            ? false
            : this.isElementInFixedContext(element.parentElement);
    }

    public setActiveSearchInput(input: HTMLInputElement): void {
        this.activeSearchInput = input;
    }

    public getActiveInputField(): HTMLInputElement {
        return this.activeSearchInput;
    }

    public getActiveInputValue(): string {
        return this.activeSearchInput?.value || '';
    }

    public isActiveInputElementInFixedContext(): boolean {
        return this.activeSearchInputIsInFixedContext;
    }

    public getIndexOfInputElement(element: HTMLInputElement): number {
        return this.searchInputs.indexOf(element);
    }

    /**
     * Add The scroll height to top if in absolute context
     */
    public calculateTopPositionFromPositionContext(top: number): number {
        return this.activeSearchInputIsInFixedContext ? top : window.innerHeight + window.pageYOffset - (window.innerHeight - top);
    }

    public setActiveInputValue(value: string): void {
        this.activeSearchInput.value = value;
    }

    public onInputTriggered(callback: (input: HTMLInputElement) => void): void {
        this.inputTriggered$.subscribe(callback);
    }

    public onInputChanged(callback: (value: string) => void): void {
        this.inputChanged$.subscribe(callback);
    }

    public onInputAutocompleted(callback: (input: HTMLInputElement) => void): void {
        this.inputAutocompleted$.subscribe(callback);
    }

    public onInputSubmitted(callback: (event: Event) => void): void {
        this.inputSubmitted$.subscribe(callback);
    }

    public onScroll(callback: () => void): void {
        this.scroll$.subscribe(callback);
    }

    public onResize(callback: () => void): void {
        this.resize$.subscribe(callback);
    }
}
